import { Box, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import {
  ApiWorkflowDialog,
  type ApiWorkflowDialogMessages,
  ComboBox,
} from '@verantos-dev/shared-components';

import { useSwitchDatasetDialog } from '../hooks/useSwitchDatasetDialog';

interface SwitchDatasetModalProps {
  projectId: string;
  currentDatasetReleaseId?: string | null;
  currentDatasetReleaseName?: string | null;
  onClose: () => void;
  onSwitched?: (datasetReleaseId: string, datasetReleaseName: string) => void;
}

/**
 * Switches the project's active dataset, offering the same dataset options as
 * the create-project picker (hence the shared `projectsList.*` message ids).
 *
 * The description line is part of the control, not decoration: the dataset
 * applies to what gets created next, and without saying so the action reads as
 * though it re-points the work already in the project.
 */
export const SwitchDatasetModal = ({
  projectId,
  currentDatasetReleaseId,
  currentDatasetReleaseName,
  onClose,
  onSwitched,
}: SwitchDatasetModalProps) => {
  const { formatMessage } = useIntl();
  const {
    releases,
    selectedRelease,
    onChangeRelease,
    onSubmit,
    closeDialog,
    isLoadingReleases,
    releasesError,
    isSubmitting,
    submittingError,
    isValidToSubmit,
    selectedDatasetName,
  } = useSwitchDatasetDialog({
    projectId,
    currentDatasetReleaseId,
    currentDatasetReleaseName,
    onClose,
    onSwitched,
  });

  const dialogMessages: ApiWorkflowDialogMessages = {
    entryCancelButtonText: formatMessage({ id: 'projectPage.switchDatasetModal.cancelButton' }),
    entrySaveButtonText: formatMessage({ id: 'projectPage.switchDatasetModal.saveButton' }),
    submittingCancelButtonText: formatMessage({
      id: 'projectPage.switchDatasetModal.cancelButton',
    }),
    submittingSaveButtonText: formatMessage({ id: 'projectPage.switchDatasetModal.submitting' }),
    successCloseButtonText: formatMessage({ id: 'projectPage.switchDatasetModal.closeButton' }),
    submittingErrorTitle: formatMessage({ id: 'projectPage.switchDatasetModal.submittingError' }),
    successTitle: formatMessage({ id: 'projectPage.switchDatasetModal.success.title' }),
    successText: formatMessage(
      { id: 'projectPage.switchDatasetModal.success.text' },
      { datasetName: selectedDatasetName }
    ),
  };

  return (
    <ApiWorkflowDialog
      id="switchDatasetModal"
      title={formatMessage({ id: 'projectPage.switchDatasetModal.title' })}
      dialogMessages={dialogMessages}
      onClose={closeDialog}
      onSubmit={onSubmit}
      isValidForSubmit={isValidToSubmit}
      isInitialLoading={isLoadingReleases}
      isSubmitting={isSubmitting}
      initialLoadingError={releasesError}
      submittingError={submittingError}
      hideDisplayButtonsOnSuccess
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
        <ComboBox
          id="switch-dataset-combobox"
          width="auto"
          label={formatMessage({ id: 'projectPage.switchDatasetModal.dataset.label' })}
          placeholder={formatMessage({ id: 'projectPage.switchDatasetModal.dataset.placeholder' })}
          value={selectedRelease}
          options={releases}
          onChange={onChangeRelease}
          showTooltip
        />
        <Typography variant="body2" color="text.secondary" data-testid="switch-dataset-description">
          {formatMessage({ id: 'projectPage.switchDatasetModal.description' })}
        </Typography>
      </Box>
    </ApiWorkflowDialog>
  );
};

export default SwitchDatasetModal;
